// src/services/privacyPolicyService.js

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const CURRENT_PRIVACY_DOC_ID = 'current';

// Cache for privacy policy data (persists until app restart)
let privacyCache = null;

/**
 * Gets the current privacy policy
 * @returns {Promise<Object|null>} Privacy policy data or null if not found
 */
export async function getCurrentPrivacyPolicy() {
  // Return cached version if available
  if (privacyCache) {
    return privacyCache;
  }

  try {
    const privacyRef = doc(db, 'privacyPolicy', CURRENT_PRIVACY_DOC_ID);
    const privacySnap = await getDoc(privacyRef);
    
    if (privacySnap.exists()) {
      const privacyData = { id: privacySnap.id, ...privacySnap.data() };
      
      // Cache the results
      privacyCache = privacyData;
      return privacyData;
    } else {
      console.error('No privacy policy found in database');
      throw new Error('Privacy policy has not been configured. Please contact an administrator.');
    }
  } catch (error) {
    console.error('Error fetching current privacy policy:', error);
    throw new Error(`Failed to fetch privacy policy: ${error.message}`);
  }
}

/**
 * Creates or updates the current privacy policy
 * @param {string} content - HTML content of the privacy policy
 * @param {string} adminEmail - Email of the admin making the update
 * @returns {Promise<Object>} Updated privacy policy data
 */
export async function updateCurrentPrivacyPolicy(content, adminEmail) {
  if (!content || content.trim().length === 0) {
    throw new Error('Privacy policy content cannot be empty');
  }

  if (!adminEmail) {
    throw new Error('Admin email is required');
  }

  try {
    const privacyRef = doc(db, 'privacyPolicy', CURRENT_PRIVACY_DOC_ID);
    
    // Get existing privacy policy to preserve version history
    let existingPrivacy = null;
    let newVersion = '1.0.0';
    let previousVersions = [];
    
    try {
      const existingSnap = await getDoc(privacyRef);
      if (existingSnap.exists()) {
        existingPrivacy = existingSnap.data();
        
        // Increment version number
        const currentVersion = existingPrivacy.version || '1.0.0';
        const versionParts = currentVersion.split('.').map(Number);
        versionParts[2] += 1; // Increment patch version
        newVersion = versionParts.join('.');
        
        // Add current version to history before updating
        previousVersions = existingPrivacy.previousVersions || [];
        previousVersions.push({
          version: existingPrivacy.version,
          content: existingPrivacy.content,
          lastUpdated: existingPrivacy.lastUpdated,
          updatedBy: existingPrivacy.updatedBy,
          archivedAt: new Date().toISOString()
        });
      }
    } catch (fetchError) {
      console.log('No existing privacy policy found, creating initial version');
    }

    const updatedPrivacy = {
      content: content.trim(),
      version: newVersion,
      lastUpdated: serverTimestamp(),
      updatedBy: adminEmail,
      isActive: true,
      previousVersions: previousVersions
    };

    // Update Firestore
    await setDoc(privacyRef, updatedPrivacy);

    // Clear cache and return updated data with timestamp converted
    privacyCache = null;
    const finalData = {
      id: CURRENT_PRIVACY_DOC_ID,
      ...updatedPrivacy,
      lastUpdated: new Date().toISOString() // Convert for immediate return
    };
    
    // Cache the new data
    privacyCache = finalData;
    
    return finalData;
  } catch (error) {
    console.error('Error updating privacy policy:', error);
    throw new Error(`Failed to update privacy policy: ${error.message}`);
  }
}

/**
 * Gets the complete version history of privacy policy
 * @returns {Promise<Array>} Array of previous versions
 */
export async function getPrivacyPolicyHistory() {
  try {
    const privacyRef = doc(db, 'privacyPolicy', CURRENT_PRIVACY_DOC_ID);
    const privacySnap = await getDoc(privacyRef);
    
    if (privacySnap.exists()) {
      const privacyData = privacySnap.data();
      return privacyData.previousVersions || [];
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching privacy policy history:', error);
    throw new Error(`Failed to fetch privacy policy history: ${error.message}`);
  }
}

/**
 * Clears the privacy policy cache (for testing or forced refresh)
 */
export function clearPrivacyCache() {
  privacyCache = null;
}

/**
 * Validates privacy policy content for basic requirements
 * @param {string} content - HTML content to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validatePrivacyContent(content) {
  const errors = [];
  
  if (!content || typeof content !== 'string') {
    errors.push('Content is required');
  } else if (content.trim().length === 0) {
    errors.push('Content cannot be empty');
  } else if (content.length < 100) {
    errors.push('Content seems too short for a privacy policy');
  } else if (content.length > 500000) {
    errors.push('Content is too long (maximum 500KB)');
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors
  };
}

/**
 * Gets statistics about the privacy policy content
 * @param {string} content - HTML content to analyze
 * @returns {Object} Statistics including word count, character count, etc.
 */
export function getPrivacyStats(content) {
  if (!content || typeof content !== 'string') {
    return {
      characterCount: 0,
      wordCount: 0,
      lineCount: 0
    };
  }
  
  // Strip HTML tags for word count
  const textOnly = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const wordCount = textOnly ? textOnly.split(' ').length : 0;
  
  return {
    characterCount: content.length,
    wordCount: wordCount,
    lineCount: content.split('\n').length
  };
}

/**
 * Default privacy policy template
 * @returns {string} Template privacy policy content
 */
export function getPrivacyPolicyTemplate() {
  return `<h1>Privacy Policy</h1>
<p><strong>Last updated:</strong> ${new Date().toLocaleDateString()}</p>

<h2>1. Introduction</h2>
<p>BackPass ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").</p>

<h2>2. Information We Collect</h2>

<h3>Personal Information</h3>
<p>We collect information you provide directly to us, such as:</p>
<ul>
  <li>Name and email address</li>
  <li>Phone number</li>
  <li>Payment information (processed securely through Stripe)</li>
  <li>Passport information (for hostel bookings)</li>
  <li>Journey preferences and booking details</li>
</ul>

<h3>Automatically Collected Information</h3>
<p>When you use our Service, we may automatically collect:</p>
<ul>
  <li>Device information (type, operating system)</li>
  <li>Usage data (features used, time spent)</li>
  <li>Location data (with your permission)</li>
  <li>IP address and browser information</li>
</ul>

<h2>3. How We Use Your Information</h2>
<p>We use the collected information to:</p>
<ul>
  <li>Process your journey bookings and payments</li>
  <li>Send booking confirmations and important updates</li>
  <li>Provide customer support</li>
  <li>Improve our Service and user experience</li>
  <li>Send marketing communications (with your consent)</li>
  <li>Comply with legal obligations</li>
</ul>

<h2>4. Information Sharing</h2>
<p>We may share your information with:</p>
<ul>
  <li><strong>Hostels:</strong> To complete your bookings</li>
  <li><strong>Payment Processors:</strong> Stripe for secure payment processing</li>
  <li><strong>Service Providers:</strong> Third parties who assist in operating our Service</li>
  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
</ul>
<p>We do not sell or rent your personal information to third parties.</p>

<h2>5. Data Security</h2>
<p>We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
<ul>
  <li>Encryption of sensitive data</li>
  <li>Regular security assessments</li>
  <li>Limited access to personal information</li>
  <li>Secure data storage practices</li>
</ul>
<p>However, no method of transmission over the Internet is 100% secure.</p>

<h2>6. Data Retention</h2>
<p>We retain your personal information for as long as necessary to:</p>
<ul>
  <li>Provide our services to you</li>
  <li>Comply with legal obligations</li>
  <li>Resolve disputes and enforce agreements</li>
</ul>
<p>When your information is no longer needed, we will securely delete or anonymize it.</p>

<h2>7. Your Rights</h2>
<p>You have the right to:</p>
<ul>
  <li>Access your personal information</li>
  <li>Correct inaccurate data</li>
  <li>Request deletion of your data</li>
  <li>Object to or restrict processing</li>
  <li>Data portability</li>
  <li>Withdraw consent at any time</li>
</ul>
<p>To exercise these rights, please contact us at privacy@backpass.co</p>

<h2>8. Children's Privacy</h2>
<p>Our Service is not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware of such collection, we will delete that information immediately.</p>

<h2>9. International Data Transfers</h2>
<p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws than your country. By using our Service, you consent to such transfers.</p>

<h2>10. Cookies and Tracking Technologies</h2>
<p>We use cookies and similar tracking technologies to:</p>
<ul>
  <li>Maintain your session</li>
  <li>Remember your preferences</li>
  <li>Analyze usage patterns</li>
  <li>Improve our Service</li>
</ul>
<p>You can control cookies through your browser settings.</p>

<h2>11. Changes to This Policy</h2>
<p>We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
<ul>
  <li>Posting the new Privacy Policy on this page</li>
  <li>Updating the "Last updated" date</li>
  <li>Sending you an email notification (for significant changes)</li>
</ul>

<h2>12. Contact Us</h2>
<p>If you have questions about this Privacy Policy, please contact us at:</p>
<ul>
  <li>Email: privacy@backpass.co</li>
  <li>Phone: +1-214-315-6968</li>
  <li>Website: https://backpass.co</li>
  <li>Address: BackPass, United States</li>
</ul>`;
}